/**
 * Accept / Edit / Reject — the console's one write control, in ONE place.
 *
 * The Review queue and Walk detail's inline queue both draw these three
 * buttons against the same `ReviewWriteTarget`. They were two copies while
 * the controls were inert, and two copies of a *disabled* button is harmless;
 * two copies of a live one is not, because the day one grows a confirmation,
 * an error line or a busy state, the other silently does not. So the control
 * is this component and neither view knows what a decision costs.
 *
 * ## Not configured is still REPORTED, and still disables
 *
 * `configured` is `writesConfigured` off the server — whether this deployment
 * has a `LEDAI_BACKEND_URL` to send a decision TO. When it is false every
 * button is genuinely `disabled` and the reason is printed beside them by the
 * view. That mechanism is unchanged from when it was the only state: what
 * changed is that `backend/` now has the route, so the reason is a deployment
 * fact rather than a missing feature. "A mutation control that appears live
 * and quietly does nothing is the single worst outcome available here"
 * (`server/backendGateway.ts`) is still the rule this obeys.
 *
 * ## Why Edit edits the TITLE
 *
 * `backend`'s four review methods take different edit fields — `fieldValues`
 * for a daily log, `priority`/`dueDate`/`assignee` for a task and a punch
 * item — but `title` is the one key ALL FOUR accept. An "Edit" that posted
 * `decision: "edited"` with no patch would record an edit that edited
 * nothing, which is a quieter version of the same lie a dead button tells. So
 * Edit opens an inline field on the row, and Save sends the title the person
 * actually typed. The richer per-kind editors belong on the record's own
 * screen; this is the queue, and the queue's job is to move rows.
 *
 * ## A decision is never optimistic
 *
 * `onDecided` re-reads the page from the server rather than patching the row
 * in place. A review queue that showed what you clicked instead of what was
 * stored would be the one screen where that matters most — and the backend
 * may legitimately answer something other than what was asked (a required
 * Procore field missing, a company no longer available), which arrives as a
 * 422 with its own sentence.
 */

import { useEffect, useState } from "react";
import { Check, ListPlus, Trash2 } from "lucide-react";
import { parseProcoreReviewValidationError } from "../../../../backend/src/shared/procoreDailyLog";
import {
  postReviewDecision,
  type ReviewDecision,
  type ReviewWriteTarget,
} from "../api";
import { C, FONT_MONO } from "../tokens";
import { notifyReviewAccepted } from "../components/ReviewSyncToast";
import {
  planRejectReasonPrompt,
  showRejectReasonPrompt,
} from "../components/RejectReasonBanner";

const LABEL: Record<string, string> = {
  approved: "Accept",
  edited: "Edit",
  rejected: "Reject",
  deferred: "Set aside",
};

export interface ReviewDecisionsProps {
  write: ReviewWriteTarget;
  /** The record's current title — what the inline editor opens with. */
  title: string;
  /** The console URL's query string, so a decision is made in the same
   *  workspace as the page that offered it. */
  search: string;
  /** `writesConfigured`. False disables every control. */
  configured: boolean;
  disabled?: boolean;
  onBusyChange?: (busy: boolean) => void;
  onValidationError?: (fieldKeys: string[]) => void;
  /** `writesNote` — printed in the button's tooltip either way, so hovering a
   *  control always says what it will do or why it cannot. */
  note: string;
  /**
   * Called after a decision LANDS, with the decision that landed.
   *
   * The argument is what lets the CARD animate its own exit. It is passed on
   * SUCCESS only, and deliberately not on the click: this endpoint really does
   * refuse decisions (a 422 with its own sentence — a required Procore field
   * missing, a company no longer available), and a card that swiped away on
   * the click would have to swipe back, which reads as a glitch rather than
   * as a refusal. The view still re-reads; it does not patch.
   */
  onDecided: (decision: ReviewDecision, startedAt: number) => void;
  /** Walk detail's inline queue is tighter than the Review queue's rows. */
  compact?: boolean;
  /**
   * Edits made inline on a review card. They travel with Accept so reviewing
   * a corrected record is one atomic action, rather than a sequence of
   * partial writes that can leave the card in an ambiguous state.
   */
  patch?: Record<string, unknown>;
  /**
   * `row` (default) is the small three-button cluster a list row carries.
   * `card` is the Review queue design's footer: Reject and Later as outline
   * pills, Accept as the ink one, and Edit demoted to a quiet text control
   * beside them — the design has no Edit, but the title edit is the one
   * mutation the chain actually accepts, so it stays where it cannot be
   * mistaken for the primary action.
   */
  variant?: "row" | "card";
  /** The card Accept's words when the record decides them — a manpower
   *  conflict reads "Accept & update" (LED-1156). Defaults to "Accept". */
  acceptLabel?: string;
  /** The record's walk + project, when the view knows them — they scope the
   *  reject-banner cap (per walk) and ride the outcome event for org
   *  attribution. Absent on views that don't carry them; the banner then
   *  caps per console session instead. */
  visitId?: string;
  jobSiteId?: string;
}

export function ReviewDecisions({
  write,
  title,
  search,
  configured,
  disabled: externallyDisabled = false,
  onBusyChange,
  onValidationError,
  note,
  onDecided,
  compact = false,
  patch,
  variant = "row",
  acceptLabel = "Accept",
  visitId,
  jobSiteId,
}: ReviewDecisionsProps) {
  const [busy, setBusy] = useState<ReviewDecision | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [draft, setDraft] = useState<string | null>(null);
  // A corrected draft should not keep showing the previous refusal.
  const patchKey = JSON.stringify(patch);
  useEffect(() => setProblem(null), [patchKey]);

  async function send(
    decision: ReviewDecision,
    patch?: Record<string, unknown>,
  ) {
    const startedAt = Date.now();
    setBusy(decision);
    onBusyChange?.(true);
    setProblem(null);
    // Reason-banner sampling is decided at the click (and only for
    // rejections); the plan rides the decision so the decided event logs
    // the probability in force whether or not the banner shows.
    const reasonPrompt =
      decision === "rejected" ? planRejectReasonPrompt(visitId) : undefined;
    const result = await postReviewDecision(
      write.kind,
      write.itemId,
      search,
      decision,
      patch,
      reasonPrompt,
    );
    setBusy(null);
    onBusyChange?.(false);
    if (!result.ok) {
      const validation = parseProcoreReviewValidationError(result.error);
      onValidationError?.(validation?.fieldKeys ?? []);
      setProblem(
        validation?.message ?? result.error ?? `That decision was refused (${result.status}).`,
      );
      return;
    }
    setDraft(null);
    if (decision === "approved" || decision === "edited") {
      notifyReviewAccepted({ itemId: write.itemId, search, startedAt });
    }
    if (decision === "rejected" && reasonPrompt?.shown) {
      // SUCCESS only, same rule as the toast above: a refused decision must
      // not be followed by a banner asking why the user made it.
      showRejectReasonPrompt({
        itemId: write.itemId,
        kind: write.kind,
        search,
        jobSiteId,
        visitId,
      });
    }
    onDecided(decision, startedAt);
  }

  const height = compact ? 26 : 27;
  const disabled = externallyDisabled || !configured || busy !== null;

  function buttonStyle(active: boolean): React.CSSProperties {
    return {
      height,
      padding: compact ? "0 9px" : "0 10px",
      borderRadius: 8,
      border: `1px solid ${active ? C.ink : C.line2}`,
      background: active ? C.ink : C.surface,
      color: active ? C.surface : configured ? C.ink : C.sub,
      fontSize: compact ? 12.65 : 13.23,
      fontWeight: 700,
      opacity: configured ? 1 : 0.5,
      cursor: configured ? "pointer" : "not-allowed",
    };
  }

  if (variant === "card" && draft === null) {
    // The design's footer: Later on its own at the left, the verdicts at the
    // right — Reject a quiet round trash, Accept the one ink pill.
    const outline: React.CSSProperties = {
      height: 38,
      borderRadius: 19,
      border: "1px solid rgba(20,17,12,.14)",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      fontFamily: "inherit",
      opacity: configured ? 1 : 0.5,
      cursor: configured ? "pointer" : "not-allowed",
    };
    return (
      <span data-testid="review-decisions" data-item-id={write.itemId} style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <span style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          {/* "Later" is a real decision — `deferred` — that the console route
              and all four backend RPCs accept. A reviewer unsure about one
              record should not have to leave a whole walk unreviewed. */}
          <button
            type="button"
            className="review-outline"
            disabled={disabled}
            data-testid="review-decision"
            data-decision="deferred"
            data-item-id={write.itemId}
            data-rpc={write.rpc}
            title={note}
            onClick={() => void send("deferred")}
            style={{ ...outline, padding: "0 15px", gap: 7, fontSize: 13.5, fontWeight: 700, letterSpacing: "-0.2px", color: C.ink }}
          >
            <ListPlus size={13} color={C.ink} aria-hidden />
            {busy === "deferred" ? "…" : "Later"}
          </button>
          <span style={{ flex: 1 }} />
          <button
            type="button"
            className="review-reject"
            disabled={disabled}
            data-testid="review-decision"
            data-decision="rejected"
            data-item-id={write.itemId}
            data-rpc={write.rpc}
            aria-label="Reject"
            title={note || "Reject"}
            onClick={() => void send("rejected")}
            style={{ ...outline, width: 38, padding: 0 }}
          >
            {busy === "rejected" ? "…" : <Trash2 size={14} aria-hidden />}
          </button>
          <button
            type="button"
            disabled={disabled}
            data-testid="review-decision"
            data-decision="approved"
            data-item-id={write.itemId}
            data-rpc={write.rpc}
            title={note}
            onClick={() => void send("approved", patch)}
            style={{ ...outline, border: 0, padding: "0 18px", gap: 7, background: C.ink, color: C.surface, fontSize: 13.5, whiteSpace: "nowrap", fontWeight: 700, letterSpacing: "-0.2px" }}
          >
            <Check size={13} color={C.surface} strokeWidth={2.6} aria-hidden />
            {busy === "approved" ? "…" : acceptLabel}
          </button>
        </span>
        {problem && (
          <span data-testid="review-decision-error" role="alert" style={{ fontSize: 13.8, color: C.red, textAlign: "right" }}>
            {problem}
          </span>
        )}
      </span>
    );
  }

  return (
    <span
      data-testid="review-decisions"
      data-item-id={write.itemId}
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-end",
        gap: 5,
        flexShrink: 0,
      }}
    >
      {/* The editor REPLACES the three buttons rather than sitting under them.
          Found by looking at the rendered page: an editor stacked beneath a
          row's controls widens that column by ~200px, which the row takes out
          of the title's `flex: 1` — the title wrapped to four lines and
          collided with the walk meta beside it. One row in, one row out keeps
          every other row on the card exactly where it was. */}
      {draft === null ? (
        <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
          {write.decisions.map((decision) => (
            <button
              key={decision}
              type="button"
              disabled={disabled}
              data-testid="review-decision"
              data-decision={decision}
              data-item-id={write.itemId}
              data-rpc={write.rpc}
              title={note}
              onClick={() => {
                if (decision === "edited") {
                  // Opens the field; it does not decide. Sending an "edited"
                  // with nothing edited is the lie this avoids.
                  setProblem(null);
                  setDraft(title);
                  return;
                }
                void send(
                  decision,
                  decision === "approved" ? patch : undefined,
                );
              }}
              style={buttonStyle(false)}
            >
              {busy === decision ? "…" : (LABEL[decision] ?? decision)}
            </button>
          ))}
        </span>
      ) : (
        <span
          data-testid="review-edit"
          style={{ display: "flex", alignItems: "center", gap: 6 }}
        >
          <input
            data-testid="review-edit-input"
            value={draft}
            // `onInput`, not `onChange`: React 19 + happy-dom never synthesises
            // `change` for a text input, and an editor no test can type into is
            // how a typed value silently stops reaching the button beside it.
            onInput={(event) =>
              setDraft((event.target as HTMLInputElement).value)
            }
            onChange={(event) =>
              setDraft((event.target as HTMLInputElement).value)
            }
            disabled={disabled}
            style={{
              width: compact ? 150 : 190,
              height,
              padding: "0 9px",
              borderRadius: 8,
              border: `1px solid ${C.ink}`,
              background: C.surface,
              color: C.ink,
              fontSize: 13.8,
            }}
          />
          <button
            type="button"
            data-testid="review-edit-save"
            disabled={disabled || draft.trim().length === 0}
            onClick={() =>
              void send("edited", { ...patch, title: draft.trim() })
            }
            style={{
              ...buttonStyle(true),
              opacity: draft.trim().length === 0 ? 0.5 : 1,
            }}
          >
            {busy === "edited" ? "…" : "Save"}
          </button>
          <button
            type="button"
            data-testid="review-edit-cancel"
            onClick={() => setDraft(null)}
            style={buttonStyle(false)}
          >
            Cancel
          </button>
        </span>
      )}

      {problem && (
        <span
          data-testid="review-decision-error"
          role="alert"
          style={{
            fontFamily: FONT_MONO,
            fontSize: 10.93,
            color: C.red,
            maxWidth: 320,
            textAlign: "right",
            lineHeight: 1.4,
          }}
        >
          {problem}
        </span>
      )}
    </span>
  );
}
